import { generateOccurrences } from './generateOccurrences';

// Stored effective dates are calendar dates, so do not parse YYYY-MM-DD as UTC.
export const budgetDateKey = (value = new Date()) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const budgetDay = value => {
  const key = budgetDateKey(value);
  return key ? new Date(`${key}T00:00:00`) : new Date(NaN);
};
export const getBudgetAmountAt = (budget, date = new Date()) => {
  const key = budgetDateKey(date);
  const version = [...(budget.amountHistory || [])]
    .filter(v => v.effectiveFrom <= key)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
  return Number(version?.amount ?? budget.amount) || 0;
};
export const getBudgetHistory = budget => [
  { effectiveFrom: budgetDateKey(budget.startDate), amount: budget.amount },
  ...(budget.amountHistory || [])
].filter((v, i, all) => !all.slice(i + 1).some(next => next.effectiveFrom === v.effectiveFrom))
  .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));

export const getNextBudgetChange = (budget, date = new Date()) =>
  getBudgetHistory(budget).find(v => v.effectiveFrom > budgetDateKey(date));

export const nextBudgetOccurrence = (budget, from = new Date()) => {
  const start = budgetDay(from);
  const limit = new Date(start.getFullYear() + 5, start.getMonth(), start.getDate());
  return generateOccurrences({ ...budget, startDate: budgetDay(budget.startDate),
    endDate: budget.endDate ? budgetDay(budget.endDate) : undefined,
    frequency: budget.isRecurring ? budget.recurringPeriod : 'once' }, start, limit)[0] || null;
};

// Sum dated amounts without changing the recurrence anchor. Proration retains
// the app's existing daily denominators (monthly actual days; quarter 91.25).
export const getBudgetAmountInRange = (budget, from, to) => {
  const start = budgetDay(from), end = budgetDay(to);
  const anchor = budgetDay(budget.startDate);
  const hardEnd = budget.endDate ? budgetDay(budget.endDate) : end;
  if ([start, end, anchor, hardEnd].some(d => Number.isNaN(d.getTime())) || end < start) return 0;
  if (budget.isRecurring && !budget.isProrated) {
    return generateOccurrences({ ...budget, startDate: anchor, endDate: hardEnd }, start, end)
      .reduce((sum, date) => sum + Math.abs(getBudgetAmountAt(budget, date)), 0);
  }
  let total = 0;
  const day = new Date(Math.max(start, anchor));
  const limit = new Date(Math.min(end, hardEnd));
  for (; day <= limit; day.setDate(day.getDate() + 1)) {
    const daysInMonth = new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate();
    const divisor = budget.isRecurring ? ({ weekly: 7, quarterly: 91.25, yearly: 365 }[budget.recurringPeriod] || daysInMonth) : daysInMonth;
    total += Math.abs(getBudgetAmountAt(budget, day)) / divisor;
  }
  return total;
};

export const hasBudgetAmountChangeBy = (budget, date) =>
  (budget.amountHistory || []).some(v => v.effectiveFrom <= budgetDateKey(date));
